import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Input, theme, Tag, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Text } = Typography;

export interface CommandItem {
  /** Eindeutige ID */
  id: string;
  /** Anzeige-Label */
  label: string;
  /** Kurze Beschreibung / Pfad */
  hint?: string;
  /** Icon */
  icon?: React.ReactNode;
  /** Tags/Labels fuer Gruppierung (z.B. "Admin", "Nav") */
  group?: string;
  /** Ziel-Navigationspfad */
  to?: string;
  /** Alternativ: freier Action-Handler */
  action?: () => void;
  /** Keywords fuer Fuzzy-Match */
  keywords?: string[];
}

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  items: CommandItem[];
}

function scoreItem(item: CommandItem, query: string): number {
  if (!query) return 1;
  const q = query.toLowerCase();
  const haystack = [
    item.label,
    item.hint ?? '',
    item.group ?? '',
    ...(item.keywords ?? []),
  ]
    .join(' ')
    .toLowerCase();

  if (haystack.includes(q)) {
    return item.label.toLowerCase().startsWith(q) ? 3 : 2;
  }
  // Fuzzy: alle Zeichen in Reihenfolge enthalten
  let idx = 0;
  for (const ch of q) {
    idx = haystack.indexOf(ch, idx);
    if (idx === -1) return 0;
    idx += 1;
  }
  return 1;
}

export function CommandPalette(props: CommandPaletteProps): React.ReactElement {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    return props.items
      .map((item) => ({ item, score: scoreItem(item, query) }))
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 30)
      .map((x) => x.item);
  }, [props.items, query]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, props.open]);

  useEffect(() => {
    if (!props.open) {
      setQuery('');
    }
  }, [props.open]);

  const runItem = (item: CommandItem): void => {
    if (item.to) {
      navigate(item.to);
    } else if (item.action) {
      item.action();
    }
    props.onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, Math.max(0, filtered.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const active = filtered[activeIndex];
      if (active) runItem(active);
    }
  };

  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLDivElement>(`[data-idx='${activeIndex}']`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const groups = useMemo(() => {
    const map = new Map<string, CommandItem[]>();
    for (const item of filtered) {
      const g = item.group ?? 'Aktionen';
      const arr = map.get(g) ?? [];
      arr.push(item);
      map.set(g, arr);
    }
    return Array.from(map.entries());
  }, [filtered]);

  let globalIndex = -1;

  return (
    <Modal
      open={props.open}
      onCancel={props.onClose}
      footer={null}
      closable={false}
      width={620}
      destroyOnClose
      styles={{
        body: { padding: 0 },
        content: { padding: 0, overflow: 'hidden', borderRadius: 14 },
      }}
      style={{ top: 120 }}
    >
      <div style={{ padding: '12px 14px', borderBottom: `1px solid ${token.colorBorderSecondary}` }}>
        <Input
          autoFocus
          size='large'
          bordered={false}
          prefix={<SearchOutlined style={{ color: token.colorTextTertiary, marginRight: 6 }} />}
          placeholder='Wohin moechtest Du? Tippe zum Suchen…'
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          suffix={
            <Tag style={{ border: 'none', background: token.colorFillSecondary, fontSize: 11 }}>
              ESC
            </Tag>
          }
        />
      </div>

      <div
        ref={listRef}
        style={{
          maxHeight: 420,
          overflowY: 'auto',
          padding: 8,
        }}
      >
        {groups.length === 0 && (
          <div style={{ padding: 48, textAlign: 'center', color: token.colorTextTertiary }}>
            Keine Treffer für "{query}"
          </div>
        )}
        {groups.map(([group, items]) => (
          <div key={group} style={{ marginBottom: 6 }}>
            <div
              style={{
                padding: '6px 10px',
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: 0.4,
                textTransform: 'uppercase',
                color: token.colorTextTertiary,
              }}
            >
              {group}
            </div>
            {items.map((item) => {
              globalIndex += 1;
              const isActive = globalIndex === activeIndex;
              return (
                <div
                  key={item.id}
                  data-idx={globalIndex}
                  onMouseEnter={() => setActiveIndex(globalIndex)}
                  onClick={() => runItem(item)}
                  style={{
                    padding: '10px 12px',
                    borderRadius: 8,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    background: isActive ? token.colorFillSecondary : 'transparent',
                    transition: 'background 0.12s ease',
                  }}
                >
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: 8,
                      background: isActive
                        ? `${token.colorPrimary}1a`
                        : token.colorFillQuaternary,
                      color: isActive ? token.colorPrimary : token.colorTextSecondary,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 14,
                      flexShrink: 0,
                    }}
                  >
                    {item.icon ?? <SearchOutlined />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, fontSize: 14, color: token.colorText }}>
                      {item.label}
                    </div>
                    {item.hint && (
                      <Text type='secondary' style={{ fontSize: 12 }}>
                        {item.hint}
                      </Text>
                    )}
                  </div>
                  {isActive && (
                    <Tag style={{ border: 'none', background: token.colorFillSecondary, fontSize: 10 }}>
                      ↵ Enter
                    </Tag>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <div
        style={{
          padding: '8px 14px',
          borderTop: `1px solid ${token.colorBorderSecondary}`,
          background: token.colorFillQuaternary,
          fontSize: 11,
          color: token.colorTextTertiary,
          display: 'flex',
          gap: 14,
          justifyContent: 'flex-end',
        }}
      >
        <span>
          <Tag style={{ fontSize: 10, border: 'none', background: token.colorFillSecondary }}>↑↓</Tag> Navigieren
        </span>
        <span>
          <Tag style={{ fontSize: 10, border: 'none', background: token.colorFillSecondary }}>↵</Tag> Auswählen
        </span>
        <span>
          <Tag style={{ fontSize: 10, border: 'none', background: token.colorFillSecondary }}>ESC</Tag> Schließen
        </span>
      </div>
    </Modal>
  );
}
